import {
  LinkRequestStatus,
  PayerPayeeLinkRequest
} from "@wingspanhq/wingspan-api-sdk";
import { linkRequestsApi } from "@api";
import { queriesLinkRequests, queriesPayeeRows, queriesPayees } from "@query";
import { useWSMutation, WSMutationConfig } from "../../../query/helpers";
import { WSServiceError } from "../../../utils/serviceHelper";
import { useXWingspanPayerId } from "../routes/RoutePayeeDetails/useXWingspanPayerId";

export type RetargetPayeeEmailInput = {
  payeeId: string;
  targetEmail: string;
};

/**
 * Changes a contractor's (payee's) invite email on v3.
 *
 * v1 used a dedicated `POST /payee/:id/email-update` that previewed the outcome
 * (`resultType`) and, when the new email already belonged to another payee,
 * merged engagements/invoices/deductions across the two relationships. v3 has no
 * such endpoint: the email change is a *retarget* of the pending invite
 * (`LinkRequestsApi.retargetLinkRequest`) which — like v1 — is only allowed
 * before the contractor signs up (a linked request is refused). Any cross-payee
 * merge now happens automatically when the contractor accepts the re-sent invite
 * (payments PR #6031), so the FE no longer previews or branches on the outcome.
 * See V3_MIGRATION_TRACKER.md (PLA-3223).
 *
 * Retarget is keyed by link-request id, not payee id, so we first look up the
 * payee's pending link request. Org/expansion context: v1 sent the org id via
 * the `X-Wingspan-User` header; the v3 equivalent is the `xWingspanAccount`
 * request scope. In the common (non-org) path `useXWingspanPayerId()` is
 * `undefined`, so no scope override is sent.
 */
export const useRetargetPayeeEmail = (
  config?: WSMutationConfig<
    PayerPayeeLinkRequest,
    WSServiceError,
    RetargetPayeeEmailInput
  >
) => {
  const xWingspanAccount = useXWingspanPayerId();

  return useWSMutation<
    PayerPayeeLinkRequest,
    WSServiceError,
    RetargetPayeeEmailInput
  >(
    async input => {
      const linkRequests = await linkRequestsApi.listPayeeLinkRequests({
        payeeId: input.payeeId,
        xWingspanAccount
      });

      const pendingLinkRequest = linkRequests.data?.find(
        linkRequest => linkRequest.status === LinkRequestStatus.PENDING
      );

      if (!pendingLinkRequest) {
        throw new Error(
          "This contractor's email can no longer be changed because they have already signed up."
        );
      }

      return linkRequestsApi.retargetLinkRequest({
        linkRequestId: pendingLinkRequest.id,
        linkRequestRetarget: { targetEmail: input.targetEmail },
        xWingspanAccount
      });
    },
    {
      dependencies: [
        queriesPayees.getPayee.queryKeyBase,
        queriesPayeeRows.searchPayeeRows.queryKeyBase,
        queriesPayees.listPayees.queryKeyBase,
        queriesLinkRequests.listPayeeLinkRequests.queryKeyBase
      ],
      ...config
    }
  );
};
